import { z } from 'zod';
import { CURRENCIES } from '../ledger';
import { PRODUCT_TYPES, RELEASE_STATES } from './enums';

/**
 * Validation messages are user-facing copy and follow section 8: state what
 * happened and what to do, plain verbs, no exclamation marks, no em dashes.
 */

export const currencySchema = z.enum(CURRENCIES);

/** Vanity slugs appear in URLs: /dev/<slug>, /@<handle>, /g/<slug>. */
export const slugSchema = z
  .string()
  .min(3, 'Use at least 3 characters.')
  .max(48, 'Use at most 48 characters.')
  .regex(
    /^[a-z0-9]+(?:-[a-z0-9]+)*$/,
    'Use lowercase letters, numbers and single hyphens between them.',
  );

export const emailSchema = z
  .string()
  .trim()
  .min(1, 'Enter your email address.')
  .max(254, 'That email address is too long.')
  .pipe(z.email('Enter a valid email address.'))
  .transform((value) => value.toLowerCase());

/**
 * Minimum length follows the Better Auth default. Creator and Developer
 * accounts additionally require 2FA (section 9), which is enforced at the auth
 * layer rather than here.
 */
export const passwordSchema = z
  .string()
  .min(8, 'Use at least 8 characters.')
  .max(128, 'Use at most 128 characters.');

export const displayNameSchema = z
  .string()
  .trim()
  .min(2, 'Use at least 2 characters.')
  .max(60, 'Use at most 60 characters.');

export const signUpInput = z.object({
  name: displayNameSchema,
  email: emailSchema,
  password: passwordSchema,
});
export type SignUpInput = z.infer<typeof signUpInput>;

export const signInInput = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Enter your password.'),
});
export type SignInInput = z.infer<typeof signInInput>;

/** Integer minor units. Every money value crossing a boundary is validated with this. */
export const minorUnitsSchema = z
  .number()
  .int('Amounts are whole minor units.')
  .max(100_000_000_000, 'That amount is out of range.');

export const nonNegativeMinorUnitsSchema = minorUnitsSchema.min(0, 'That amount is out of range.');

/** Section 5.2. Rates are integer basis points, never floats. */
export const basisPointsSchema = z
  .number()
  .int('Rates are whole basis points.')
  .min(0, 'A rate cannot be negative.')
  .max(10_000, 'A rate cannot exceed 100%.');

/** Section 5.2. Every listed title must offer at least 5% to creators. */
export const listedCreatorRateSchema = basisPointsSchema.min(
  500,
  'Every listed title offers creators at least 5%.',
);

export const productTypeSchema = z.enum(PRODUCT_TYPES);
export const releaseStateSchema = z.enum(RELEASE_STATES);

/*
  `serviceTierSchema` was here and went with the studios (#144). It parsed
  `self_serve` or `full_service`, which decided how a sale was divided between
  GOODBUILD and the company whose game it was. There is no second party.
*/
